import { faBan, faKey, faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';

import useClientApiDocsAccess from '../../../api/super-admin/clients/useClientApiDocsAccess';
import useClientApiKeys from '../../../api/super-admin/clients/useClientApiKeys';
import MainButton from '../../../components/agv-button';
import InputText from '../../../components/agv-forms-input';
import GenericModal from '../../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import { StatusDot } from '../../../redesign-v4/components/data-grid';
import {
  buildDocsUrl,
  describePasswordGeneration,
  DOCS_LOGIN_HINT,
} from '../api-docs-utils';
import {
  buildCreatePayload,
  buildUpdatePayload,
  describeRateLimit,
  EMPTY_CREATE_FORM,
  EXPIRY_PRESET,
  expiryFromPreset,
  formatExpiryDate,
  getKeyDisplayName,
  getKeyStatus,
  getMinExpiryDate,
  KEY_TYPE,
  KEY_TYPE_LABEL,
  toEditForm,
  validateCreateForm,
  validateEditForm,
} from '../api-key-utils';
import { formatDateTime } from '../client-diff';
import { Banner, CopyField, FormSection, Panel, Toggle } from '../clients-ui';

const FIELD_LABEL_CLASS =
  'tw-text-sm tw-text-text-dark-medium-importance tw-mb-1';
const FIELD_ERROR_CLASS = 'tw-m-0 tw-text-[12px] tw-text-input-required';
const FIELD_HINT_CLASS = 'tw-m-0 tw-text-[11px] tw-text-[#76808c]';

/** End-of-validity date: date input, shortcuts, and "Aucune" for a key with no expiry. */
const ExpiryField = ({ value, onChange, error }) => (
  <div className="tw-flex tw-flex-col">
    <span className={FIELD_LABEL_CLASS}>Fin de validité</span>
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <input
        type="date"
        aria-label="Fin de validité"
        min={getMinExpiryDate()}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="tw-rounded-lg tw-border-solid tw-border-input-border tw-bg-input-background tw-px-3 tw-py-1.5 tw-text-sm focus:tw-border-input-borderFocused"
      />
      <MainButton
        variant="secondary"
        text="90 jours"
        onClick={() => onChange(expiryFromPreset(EXPIRY_PRESET.DAYS_90))}
      />
      <MainButton
        variant="secondary"
        text="1 an"
        onClick={() => onChange(expiryFromPreset(EXPIRY_PRESET.YEAR_1))}
      />
      <MainButton
        variant="secondary"
        text="Aucune"
        onClick={() => onChange(expiryFromPreset(EXPIRY_PRESET.NONE))}
      />
    </div>
    {error ? (
      <p className={`${FIELD_ERROR_CLASS} tw-mt-2`}>{error}</p>
    ) : (
      <p className={`${FIELD_HINT_CLASS} tw-mt-2`}>
        {value
          ? 'La clé reste valable toute la journée choisie (UTC).'
          : 'Sans date, la clé n’expire jamais.'}
      </p>
    )}
  </div>
);

/** Calls per second allowed for the key; empty = default limit of the public API. */
const RateLimitField = ({ value, onChange, bounds, error }) => (
  <div className="tw-flex tw-flex-col">
    <InputText
      value={value}
      setValue={onChange}
      fieldTitle="Limite de débit (appels par seconde)"
      placeholder={`Défaut : ${bounds.default}`}
    />
    <p className={error ? FIELD_ERROR_CLASS : FIELD_HINT_CLASS}>
      {error ||
        `Laissez vide pour la limite par défaut (${bounds.default} appels/s). Entre ${bounds.min} et ${bounds.max}. La limite est comptée par instance de l’API.`}
    </p>
  </div>
);

/** Scopes of a limited key, drawn from the catalogue supplied by the API. */
const ScopePicker = ({ catalogue, selected, onChange, error }) => (
  <div className="tw-flex tw-flex-col tw-gap-2">
    <span className={FIELD_LABEL_CLASS}>Scopes autorisés</span>
    {catalogue.map(({ scope, label, description }) => (
      <Toggle
        key={scope}
        checked={selected.includes(scope)}
        onChange={(isChecked) =>
          onChange(
            isChecked
              ? [...selected, scope]
              : selected.filter((item) => item !== scope)
          )
        }
        label={`${label} (${scope})`}
        description={description}
      />
    ))}
    {error && <p className={FIELD_ERROR_CLASS}>{error}</p>}
  </div>
);

const KeyTypePicker = ({ value, onChange }) => (
  <div className="tw-flex tw-flex-col">
    <span className={FIELD_LABEL_CLASS}>Type de clé</span>
    <div className="tw-flex tw-flex-col tw-gap-2">
      {[
        {
          type: KEY_TYPE.FULL,
          label: 'Clé complète',
          description: 'Accès à toutes les routes de l’API publique.',
        },
        {
          type: KEY_TYPE.SCOPED,
          label: 'Clé limitée',
          description: 'Accès restreint aux scopes choisis ci-dessous.',
        },
      ].map(({ type, label, description }) => (
        <div key={type} className="tw-flex tw-items-start tw-gap-2">
          <input
            id={`api-key-type-${type}`}
            aria-describedby={`api-key-type-${type}-description`}
            type="radio"
            name="api-key-type"
            checked={value === type}
            onChange={() => onChange(type)}
            className="tw-mt-0.5 tw-size-4 tw-cursor-pointer tw-accent-main"
          />
          <span className="tw-flex tw-flex-col">
            <label
              htmlFor={`api-key-type-${type}`}
              className="tw-cursor-pointer tw-text-[13px] tw-text-black"
            >
              {label}
            </label>
            <span
              id={`api-key-type-${type}-description`}
              className="tw-text-[11px] tw-text-[#76808c]"
            >
              {description}
            </span>
          </span>
        </div>
      ))}
    </div>
  </div>
);

/** "Créée le 22/10/2026 14:30 par Camille", or a note for a key that predates the tool. */
const describeCreation = (apiKey) => {
  if (!apiKey.created_at) return 'Créée avant l’outil';

  const author = apiKey.created_by?.name;
  const by = author ? ` par ${author}` : '';

  return `Créée le ${formatDateTime(apiKey.created_at)}${by}`;
};

/** When the key was last used, or why there is no date. */
const describeLastUse = (apiKey) => {
  if (apiKey.last_used_at) {
    return `Dernière utilisation ${formatDateTime(apiKey.last_used_at)}`;
  }

  return apiKey.is_legacy
    ? 'Utilisation non encore enregistrée'
    : 'Jamais utilisée';
};

const IconButton = ({ icon, title, onClick, disabled, isDanger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={`tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-[5px] tw-border tw-border-solid tw-border-transparent tw-bg-transparent tw-text-[#76808c] disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ${
      isDanger
        ? 'hover:tw-border-red-200 hover:tw-bg-red-50 hover:tw-text-red-600'
        : 'hover:tw-border-[#e6e8ec] hover:tw-bg-[rgba(118,128,140,0.06)] hover:tw-text-black'
    }`}
  >
    <FontAwesomeIcon icon={icon} className="tw-size-3.5" />
  </button>
);

const describeExpiry = (apiKey, status) => {
  if (status.code === 'REVOKED') {
    return apiKey.revoked_at
      ? `Révoquée le ${formatDateTime(apiKey.revoked_at)}`
      : 'Révoquée';
  }
  if (!apiKey.expires_at) return 'Sans expiration';

  return `${status.code === 'EXPIRED' ? 'Expirée' : 'Expire'} le ${formatExpiryDate(apiKey.expires_at)}`;
};

const ApiKeyRow = ({ apiKey, isBusy, onEdit, onRevoke }) => {
  const status = getKeyStatus(apiKey);
  const isRevoked = status.code === 'REVOKED';

  return (
    <div
      className={`tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-4 tw-py-3 last:tw-border-b-0 ${isRevoked ? 'tw-opacity-60' : ''}`}
    >
      <span
        className={`tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-white ${apiKey.is_legacy ? 'tw-bg-[#76808c]' : 'tw-bg-main'}`}
      >
        <FontAwesomeIcon icon={faKey} className="tw-size-3.5" />
      </span>

      <span className="tw-flex tw-min-w-[220px] tw-flex-1 tw-flex-col tw-gap-1">
        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-[13px] tw-font-medium tw-text-black">
            {getKeyDisplayName(apiKey)}
          </span>
          <span className="ic-chip">{KEY_TYPE_LABEL[apiKey.key_type]}</span>
          {apiKey.is_legacy && (
            <span
              className="tw-rounded-[3px] tw-bg-amber-50 tw-px-1.5 tw-py-px tw-text-[11px] tw-font-medium tw-text-amber-800"
              title="Clé créée avant cet outil : elle sert à une interface existante, pas à l’API publique."
            >
              Héritée · {apiKey.project}
            </span>
          )}
          {apiKey.scopes.map((scope) => (
            <span key={scope} className="ic-chip">
              {scope}
            </span>
          ))}
        </span>
        <span className="tw-font-mono tw-text-[11px] tw-text-[#76808c]">
          {apiKey.prefix ? `${apiKey.prefix}…` : '—'}
        </span>
        <span className="tw-text-[11px] tw-text-[#76808c]">
          {describeCreation(apiKey)}
          {' · '}
          {describeLastUse(apiKey)}
        </span>
      </span>

      <span className="tw-flex tw-w-[210px] tw-flex-col tw-whitespace-nowrap tw-text-[12px]">
        <StatusDot color={status.color} label={status.label} />
        <span className="tw-text-[11px] tw-text-[#76808c]">
          {describeExpiry(apiKey, status)}
        </span>
        <span className="tw-text-[11px] tw-text-[#76808c]">
          {describeRateLimit(apiKey)}
        </span>
      </span>

      {!isRevoked && (
        <span className="tw-flex tw-gap-1">
          <IconButton
            icon={faPen}
            title="Modifier la clé"
            onClick={() => onEdit(apiKey)}
            disabled={isBusy}
          />
          <IconButton
            icon={faBan}
            title="Révoquer la clé"
            onClick={() => onRevoke(apiKey)}
            disabled={isBusy}
            isDanger
          />
        </span>
      )}
    </div>
  );
};

/**
 * Shown instead of the content when a load failed: without it a failed load looks like "this
 * tenant has no key" (an empty list) or like an endless "loading", and an administrator could
 * conclude that a tenant has no interface key when the call simply did not go through.
 */
const LoadFailure = ({ message, onRetry }) => (
  <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
    <Banner tone="warn">{message}</Banner>
    <MainButton variant="secondary" text="Réessayer" onClick={onRetry} />
  </div>
);

/** Confirmation before regenerating the documentation password. */
const RegeneratePasswordModal = ({ isRegenerating, onConfirm, onClose }) => (
  <GenericModal
    isOpen
    setIsOpen={(isOpen) => !isOpen && onClose()}
    title="Régénérer le mot de passe"
    size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
    secondaryActionText="Annuler"
    secondaryAction={onClose}
    mainActionText="Régénérer"
    mainActionVariant="danger"
    mainAction={onConfirm}
    mainActionIsLoading={isRegenerating}
  >
    <p className="tw-m-0 tw-text-sm">
      L’ancien mot de passe sera refusé immédiatement, et les personnes
      connectées à la documentation devront se reconnecter. Le nouveau mot de
      passe s’affichera une seule fois : pensez à le transmettre au client.
    </p>
  </GenericModal>
);

/**
 * Tenant access to the public API documentation (Scalar, in English), behind a login and a password
 * specific to the tenant.
 *
 * Enabling creates the access: the login comes from the tenant name, the password is generated and
 * is shown only once (after that only its hash exists). Disabling cuts the access without losing
 * the password; regenerating replaces it and closes open sessions. Each action applies immediately,
 * as with the keys: nothing goes through "Relire et enregistrer".
 */
const DocsAccessSection = ({ clientId, onSecretIssued }) => {
  const {
    access,
    isLoading,
    loadError,
    reload,
    isSaving,
    setEnabled,
    regeneratePassword,
  } = useClientApiDocsAccess(clientId);
  const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);

  const docsUrl = access
    ? buildDocsUrl(process.env.REACT_APP_BASE_URL, access.docs_path)
    : '';

  /**
   * Hands a freshly generated password up to the client sheet, which displays it. The text of the
   * modal is built here, from the answer of the API, because this component may be gone (tab
   * changed) by the time the answer arrives.
   */
  const issuePassword = ({ password, access: issuedAccess }) => {
    const url = buildDocsUrl(
      process.env.REACT_APP_BASE_URL,
      issuedAccess.docs_path
    );

    onSecretIssued({
      id: `docs-password-${issuedAccess.client_id ?? clientId}-${issuedAccess.password_generated_at}`,
      title: 'Mot de passe de la documentation',
      label: 'Mot de passe',
      secret: password,
      warning:
        'Ce mot de passe ne sera plus jamais affiché : seule son empreinte est conservée. Copiez-le maintenant et transmettez-le de façon sécurisée.',
      hint: `Le client se connecte sur ${url} avec l’identifiant « ${issuedAccess.login_id} » (à saisir tel quel : minuscules et tirets) et ce mot de passe. Pour le mot de passe seulement, les majuscules et les séparateurs n’ont pas d’importance.`,
      confirmLabel: 'J’ai copié le mot de passe et je l’ai rangé en lieu sûr',
    });
  };

  const handleToggle = async (isEnabled) => {
    if (isSaving) return;

    const result = await setEnabled(isEnabled);

    if (result?.password) issuePassword(result);
  };

  const handleRegenerate = async () => {
    const result = await regeneratePassword();

    if (result) {
      setIsRegenerateOpen(false);
      issuePassword(result);
    }
  };

  const renderBody = () => {
    if (loadError) {
      return (
        <LoadFailure
          message="L’accès à la documentation n’a pas pu être chargé."
          onRetry={reload}
        />
      );
    }

    if (isLoading || !access) {
      return (
        <p className="tw-m-0 tw-text-[13px] tw-text-[#76808c]">
          Chargement de l’accès…
        </p>
      );
    }

    return (
      <>
        <Toggle
          checked={access.is_enabled}
          onChange={handleToggle}
          label="Accès à la documentation"
          description={
            access.is_configured
              ? 'Décochez pour couper l’accès : le mot de passe est conservé.'
              : 'À l’activation, un identifiant est créé et un mot de passe est généré (affiché une seule fois).'
          }
        />
        {access.is_configured && (
          <>
            {!access.is_enabled && (
              <Banner>
                L’accès est désactivé : le client ne peut plus se connecter.
              </Banner>
            )}
            <CopyField label="Adresse de la documentation" value={docsUrl} />
            <CopyField label="Identifiant" value={access.login_id} />
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
              <span className="tw-text-[11px] tw-text-[#76808c]">
                {describePasswordGeneration(access)}
              </span>
              <MainButton
                variant="secondary"
                text="Régénérer le mot de passe"
                onClick={() => setIsRegenerateOpen(true)}
                disabled={isSaving}
              />
            </div>
            <p className="tw-m-0 tw-text-[11px] tw-text-[#76808c]">
              {DOCS_LOGIN_HINT}
            </p>
          </>
        )}
      </>
    );
  };

  return (
    <FormSection
      title="Documentation de l’API"
      description="Les développeurs du client lisent la documentation (en anglais) derrière un identifiant et un mot de passe propres à ce client."
    >
      {renderBody()}

      {isRegenerateOpen && (
        <RegeneratePasswordModal
          isRegenerating={isSaving}
          onConfirm={handleRegenerate}
          onClose={() => setIsRegenerateOpen(false)}
        />
      )}
    </FormSection>
  );
};

const EditKeyModal = ({ apiKey, options, isSaving, onSave, onClose }) => {
  const [form, setForm] = useState(() => toEditForm(apiKey));
  const update = (field) => (value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const { isValid, errors } = validateEditForm(apiKey, form, options);
  const payload = buildUpdatePayload(apiKey, form);
  const hasChanges = Object.keys(payload).length > 0;
  const isPublicKey = !apiKey.is_legacy;

  return (
    <GenericModal
      isOpen
      setIsOpen={(isOpen) => !isOpen && onClose()}
      title={`Modifier la clé « ${getKeyDisplayName(apiKey)} »`}
      size={MODAL_SIZE_STYLE[MODAL_SIZE.LG]}
      secondaryActionText="Annuler"
      secondaryAction={onClose}
      mainActionText="Enregistrer"
      mainAction={() => onSave(payload)}
      mainActionDisabled={!isValid || !hasChanges}
      mainActionIsLoading={isSaving}
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {apiKey.is_legacy && (
          <Banner>
            Clé historique : seuls le libellé et la fin de validité peuvent être
            modifiés. La limite de débit et les scopes ne concernent que l’API
            publique.
          </Banner>
        )}
        <InputText
          value={form.label}
          setValue={update('label')}
          fieldTitle="Libellé"
          placeholder={apiKey.project}
          isRequired={isPublicKey}
          emptyError="Le libellé est obligatoire."
          maxLength={100}
        />
        {!isPublicKey && errors.label && (
          <p className={FIELD_ERROR_CLASS}>{errors.label}</p>
        )}
        <ExpiryField
          value={form.expires_at}
          onChange={update('expires_at')}
          error={errors.expires_at}
        />
        {isPublicKey && (
          <RateLimitField
            value={form.rate_limit_rps}
            onChange={update('rate_limit_rps')}
            bounds={options.rate_limit_rps}
            error={errors.rate_limit_rps}
          />
        )}
        {isPublicKey && apiKey.key_type === KEY_TYPE.SCOPED && (
          <ScopePicker
            catalogue={options.scopes}
            selected={form.scopes}
            onChange={update('scopes')}
            error={errors.scopes}
          />
        )}
      </div>
    </GenericModal>
  );
};

/**
 * Revocation: immediate and permanent. The key name must be typed to confirm, and a legacy key
 * warns that an existing interface may still be using it (last use).
 */
const RevokeKeyModal = ({ apiKey, isRevoking, onConfirm, onClose }) => {
  const [typedName, setTypedName] = useState('');
  const name = getKeyDisplayName(apiKey);

  return (
    <GenericModal
      isOpen
      setIsOpen={(isOpen) => !isOpen && onClose()}
      title="Révoquer la clé d’API"
      size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
      secondaryActionText="Annuler"
      secondaryAction={onClose}
      mainActionText="Révoquer la clé"
      mainActionVariant="danger"
      mainAction={onConfirm}
      mainActionDisabled={typedName.trim() !== name}
      mainActionIsLoading={isRevoking}
    >
      <div className="tw-flex tw-flex-col tw-gap-3">
        <p className="tw-m-0 tw-text-sm">
          La clé « {name} » sera refusée immédiatement, sans retour possible.
          Pour la remplacer, générez d’abord une nouvelle clé.
        </p>
        {apiKey.is_legacy && (
          <Banner tone="warn">
            Cette clé existait avant l’outil : elle sert à une interface
            existante (projet {apiKey.project}).{' '}
            {apiKey.last_used_at
              ? `Dernière utilisation : ${formatDateTime(apiKey.last_used_at)}.`
              : 'Aucune utilisation enregistrée pour l’instant (le suivi vient d’être mis en place : l’interface peut l’utiliser sans que cela se voie encore).'}{' '}
            Si cette interface l’utilise encore, elle sera coupée dès
            maintenant.
          </Banner>
        )}
        <InputText
          value={typedName}
          setValue={setTypedName}
          fieldTitle={`Pour confirmer, saisissez « ${name} »`}
          placeholder={name}
        />
      </div>
    </GenericModal>
  );
};

/**
 * Tenant API keys.
 *
 * - List: the public API keys and the legacy keys ("Héritée" badge), revoked ones included, with
 *   their status, their end of validity, their rate limit and their last use.
 * - Generation: label, type (full or limited to scopes), end of validity, rate limit. The key is
 *   shown only once, at creation.
 * - Immediate edit and revocation (revocation after confirmation by typing the name), traced in
 *   the tenant history without ever recording a key.
 *
 * The keys do not go through "Relire et enregistrer": each action applies immediately.
 */
export default function ApiKeysStep({ clientId, onSecretIssued }) {
  const {
    apiKeys,
    options,
    isLoading,
    loadError,
    reload,
    isCreating,
    busyKeyId,
    createApiKey,
    updateApiKey,
    revokeApiKey,
  } = useClientApiKeys(clientId);

  const [form, setForm] = useState(EMPTY_CREATE_FORM);
  // The "empty field" error only appears once the label has been typed and then cleared.
  const [isLabelTouched, setIsLabelTouched] = useState(false);
  const [keyToEdit, setKeyToEdit] = useState(null);
  const [keyToRevoke, setKeyToRevoke] = useState(null);

  const update = (field) => (value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const { isValid, errors } = options
    ? validateCreateForm(form, options)
    : { isValid: false, errors: {} };
  const hasLegacyKeys = apiKeys.some((apiKey) => apiKey.is_legacy);

  const handleCreate = async () => {
    const created = await createApiKey(buildCreatePayload(form));

    if (created) {
      setForm(EMPTY_CREATE_FORM);
      setIsLabelTouched(false);
      // Handed up to the client sheet, which displays it: this tab may be gone (changed) by the time
      // the answer arrives, and the key is shown only once.
      onSecretIssued({
        id: `api-key-${created.apiKey.id}`,
        title: 'Votre clé d’API',
        label: 'Clé d’API',
        secret: created.key,
        warning:
          'Cette clé ne sera plus jamais affichée : seule son empreinte est conservée. Copiez-la maintenant et transmettez-la de façon sécurisée.',
        hint: (
          <>
            Elle s’envoie dans l’en-tête{' '}
            <span className="tw-font-mono">
              Authorization: Bearer &lt;clé&gt;
            </span>{' '}
            ou <span className="tw-font-mono">X-API-Key: &lt;clé&gt;</span>.
          </>
        ),
        confirmLabel: 'J’ai copié la clé et je l’ai rangée en lieu sûr',
      });
    }
  };

  const handleSave = async (payload) => {
    if (await updateApiKey(keyToEdit.id, payload)) setKeyToEdit(null);
  };

  const handleRevoke = async () => {
    if (await revokeApiKey(keyToRevoke.id)) setKeyToRevoke(null);
  };

  const renderKeys = () => {
    if (loadError) {
      return (
        <LoadFailure
          message="Les clés d’API n’ont pas pu être chargées. Cela ne veut pas dire que ce client n’en a pas : réessayez."
          onRetry={reload}
        />
      );
    }

    if (isLoading) {
      return (
        <p className="tw-m-0 tw-text-[13px] tw-text-[#76808c]">
          Chargement des clés…
        </p>
      );
    }

    if (apiKeys.length === 0) {
      return (
        <Banner>
          Aucune clé pour ce client : générez-en une ci-dessous pour ouvrir
          l’API publique.
        </Banner>
      );
    }

    return (
      <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
        {apiKeys.map((apiKey) => (
          <ApiKeyRow
            key={apiKey.id}
            apiKey={apiKey}
            isBusy={busyKeyId === apiKey.id}
            onEdit={setKeyToEdit}
            onRevoke={setKeyToRevoke}
          />
        ))}
      </div>
    );
  };

  return (
    <Panel>
      <FormSection
        title="Clés d’API"
        description="Elles permettent à un système externe d’appeler l’API publique de ce client. Plusieurs clés peuvent être actives en même temps, pour changer de clé sans coupure."
      >
        {hasLegacyKeys && (
          <Banner>
            Les clés « Héritées » existaient avant cet outil : elles servent aux
            interfaces existantes (batchs, Digitappro, Cooperl…) et ne
            fonctionnent pas sur l’API publique. Révoquer l’une d’elles coupe
            l’interface qui l’utilise.
          </Banner>
        )}
        {renderKeys()}
      </FormSection>

      {options && (
        <FormSection
          title="Générer une clé"
          description="La clé n’est affichée qu’une seule fois, à la création : seule son empreinte est conservée."
        >
          <InputText
            value={form.label}
            setValue={(value) => {
              setIsLabelTouched(true);
              update('label')(value);
            }}
            fieldTitle="Libellé"
            isRequired
            emptyError={
              isLabelTouched ? 'Le libellé est obligatoire.' : undefined
            }
            placeholder="ERP entrepôt, Keris…"
            maxLength={100}
          />
          <KeyTypePicker value={form.key_type} onChange={update('key_type')} />
          {form.key_type === KEY_TYPE.SCOPED && (
            <ScopePicker
              catalogue={options.scopes}
              selected={form.scopes}
              onChange={update('scopes')}
              error={errors.scopes}
            />
          )}
          <ExpiryField
            value={form.expires_at}
            onChange={update('expires_at')}
            error={errors.expires_at}
          />
          <RateLimitField
            value={form.rate_limit_rps}
            onChange={update('rate_limit_rps')}
            bounds={options.rate_limit_rps}
            error={errors.rate_limit_rps}
          />
          <div className="tw-flex tw-justify-end">
            <MainButton
              text="Générer la clé"
              onClick={handleCreate}
              disabled={!isValid}
              isLoading={isCreating}
            />
          </div>
        </FormSection>
      )}

      <DocsAccessSection clientId={clientId} onSecretIssued={onSecretIssued} />

      {keyToEdit && (
        <EditKeyModal
          key={keyToEdit.id}
          apiKey={keyToEdit}
          options={options}
          isSaving={busyKeyId === keyToEdit.id}
          onSave={handleSave}
          onClose={() => setKeyToEdit(null)}
        />
      )}
      {keyToRevoke && (
        <RevokeKeyModal
          key={keyToRevoke.id}
          apiKey={keyToRevoke}
          isRevoking={busyKeyId === keyToRevoke.id}
          onConfirm={handleRevoke}
          onClose={() => setKeyToRevoke(null)}
        />
      )}
    </Panel>
  );
}
